"use client";

import { useLogin } from "@kaplan-labs/up-auth-api-client";
import { PaperPlaneTiltIcon } from "@phosphor-icons/react/dist/ssr/PaperPlaneTilt";
import { QuestionIcon } from "@phosphor-icons/react/dist/ssr/Question";
import { HTTPError } from "ky";
import { useId } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { logError, logWarn, useTheme } from "@up/data";
import { Label } from "@up/form";

import { BoundaryFallback } from "../boundary-fallback";
import { Spinner } from "../spinner/spinner";
import { useAutoFocus } from "../use-autofocus";

import type { ErrorInfo, RefObject } from "react";

const formatOTPErrorMessage = function (message: string) {
  if (message.match(/401/)) {
    return "Invalid code. Please try again";
  } else {
    return message;
  }
};

const logBoundaryError = (error: unknown, info: ErrorInfo) => {
  logError({ action: "AUTH_BOUNDARY_ERROR", error, context: { up_stack: info.componentStack } });
};

type Props = {
  cancel?: () => void;
  email?: string;
  onSuccess?: () => void;
};

function Go({ cancel, email = "", onSuccess }: Props) {
  const theme = useTheme();
  const heading = useAutoFocus();

  const {
    activeField,
    email: emailValue,
    emailErrorMessage,
    otpErrorMessage,
    onSubmit,
    isLoading,
    onChange,
    otp,
    reset,
  } = useLogin({
    initialState: {
      email,
      otpMetadata: {
        partnerId: process.env[
          "NEXT_PUBLIC_HALL_OF_RECORDS_PARTNER_ID"
        ] as string,
      },
    },
    onSuccess() {
      onSuccess?.();
    },
    onException(error) {
      switch (error.type) {
        case "incorrect_otp":
        case "expired_otp":
          break;
        case "http_error":
        case "network_error":
          logError({ action: "AUTH_NETWORK_ERROR", error });
          break;
        case "unknown_error":
          logError({ action: "AUTH_UNKNOWN_ERROR", error });
      }
    },
  });

  const emailId = useId();

  return (
    <form onSubmit={onSubmit}>
      {activeField === "email" && (
        <>
          <h2
            className={theme.heading2Class}
            tabIndex={-1}
            ref={heading as RefObject<HTMLHeadingElement>}
          >
            Verify your email
          </h2>
          <p className="mb-8 opacity-80 leading-snug">
            In order to keep your information safe, enter your email to receive
            a unique, single-use password.
          </p>
          <div>
            <Label
              htmlFor={emailId}
              style={{ display: "block" }}
              text="Email Address"
            />
            <input
              className="kup-input-text"
              id={emailId}
              name="email"
              onChange={onChange}
              type="email"
              value={emailValue}
            />
            <br />
          </div>
        </>
      )}
      {activeField === "otp" && (
        <div>
          <h2
            className={theme.heading2Class}
            tabIndex={-1}
            ref={heading as RefObject<HTMLHeadingElement>}
          >
            Single-use password sent to:
          </h2>
          <p className="text-lg text-ellipsis overflow-hidden">{emailValue}</p>

          <br />

          <div>
            <label className="mb-1 block" htmlFor="otp">
              Single-use password
            </label>
            <input
              autoComplete="off"
              className="kup-input-text  kup-font-monospace block w-full text-center"
              id="otp"
              name="otp"
              onChange={onChange}
              style={{
                width: "12rem",
                fontSize: "1.5rem",
              }}
              type="text"
              value={otp}
            />
          </div>

          <br />
          {otpErrorMessage ? (
            <p className="mb-4" style={{ color: "#d00" }}>
              {formatOTPErrorMessage(otpErrorMessage)}
            </p>
          ) : null}
        </div>
      )}

      {emailErrorMessage ? (
        <p className="mb-4 leading-snug">
          <span className="text-red-500 font-bold">{emailErrorMessage}.</span>
          <br />
          If you need assistance please contact us at{" "}
          <a className="underline" href={`mailto:${theme.supportEmail}`}>
            {theme.supportEmail}
          </a>
        </p>
      ) : null}

      {activeField === "email" ? (
        <p className="mb-5 opacity-70 text-sm leading-tight">
          By pressing &quot;Submit Email&quot; below, which you acknowledge is
          your electronic signature and legally valid, you agree to the{" "}
          {theme.partnerOnlineName}{" "}
          <a href="/privacy-policy" className="underline">
            Privacy Policy
          </a>{" "}
          (
          <a
            href="/privacy-policy-addendum-for-california-residents"
            className="underline"
          >
            California Notice At Collection
          </a>
          ), which governs the processing and handling of your data, and consent
          to the transfer of your data to the United States. You{" "}
          {theme.noGuardian ? null : <>(or your parent/guardian)</>} may revoke
          this consent at any time by contacting our service provider at{" "}
          <a
            className="underline"
            href="mailto:privacy.universitypartners@kaplan.com"
          >
            privacy.universitypartners@kaplan.com
          </a>
          .
        </p>
      ) : null}

      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            className={`${theme.buttonPrimaryClass} up-index-hero-apply-now-button`}
            disabled={isLoading}
            style={{
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.8 : 1,
            }}
            type="submit"
          >
            {activeField === "otp" && "Submit Code"}
            {activeField === "email" && "Submit Email"}
          </button>
          {isLoading && <Spinner className="ml-3" />}
        </div>

        {cancel ? (
          <button
            className="px-2 underline"
            disabled={isLoading}
            onClick={cancel}
            style={{
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.8 : 1,
            }}
          >
            Cancel
          </button>
        ) : null}
      </div>

      {activeField === "otp" ? (
        <div className="animation-fade-in mt-7 pt-6 mb-6 flex flex-col gap-5 leading-snug border-t border-neutral-400">
          <div className="flex items-center gap-4 opacity-80">
            <div className="shink-0">
              <PaperPlaneTiltIcon size={24} color="#000" />
            </div>
            <div className="overflow-hidden">
              <p>
                <strong>Email will be sent from:</strong>
              </p>
              <p className="text-ellipsis overflow-hidden">
                {theme.supportEmail}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 opacity-80">
            <div className="shink-0">
              <QuestionIcon size={24} color="#000" />
            </div>
            <p>
              <strong>Don't see the email?</strong>
              <br />
              Check your spam and filtered folders. Having trouble?{" "}
              <button
                className="m-0 p-0 underline"
                onClick={reset}
                type="button"
              >
                Re-Enter Your Email
              </button>
            </p>
          </div>
        </div>
      ) : null}
      <br />
      <br />
    </form>
  );
}

export function Auth(props: Props) {
  return (
    <ErrorBoundary
      FallbackComponent={BoundaryFallback}
      onError={logBoundaryError}
    >
      <Go {...props} />
    </ErrorBoundary>
  );
}
